import { useEffect, useState } from "react";
import { Cake, Gift, TrendingUp } from "lucide-react";
import { subMonths, parseISO, differenceInCalendarDays, addYears, setYear } from "date-fns";
import { toast } from "sonner";
import { useCollection, usePrefs } from "@/hooks/useCollection";
import { rupiah, sum, monthKey, fmtDate, EXPENSE_CATS } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { EntryForm, ItemList, Section, Stat } from "./shared";

const daysUntil = (d) => {
  const today = new Date();
  let next = setYear(parseISO(d), today.getFullYear());
  if (differenceInCalendarDays(next, today) < 0) next = addYears(next, 1);
  return differenceInCalendarDays(next, today);
};

const Prediction = ({ birthdays, month }) => {
  const { items: expenses } = useCollection("expenses");
  const { items: budgets } = useCollection("budgets");
  const mm = month.slice(5);
  const bdays = birthdays.filter((b) => b.date.slice(5, 7) === mm);
  const gifts = sum(bdays, "gift");
  const base = parseISO(`${month}-01`);
  const past = [1, 2, 3].map((i) => monthKey(subMonths(base, i)));
  const pastExp = expenses.filter((e) => past.some((p) => e.date.startsWith(p)));
  const monthsWithData = past.filter((p) => expenses.some((e) => e.date.startsWith(p))).length || 1;
  const avg = sum(pastExp) / monthsWithData;
  const budget = sum(budgets.filter((b) => b.month === month));
  const total = avg + gifts;
  const cats = EXPENSE_CATS.map((c) => [c, sum(pastExp.filter((e) => e.category === c)) / monthsWithData]).filter(([, v]) => v > 0);

  return (
    <Section title={<span className="flex items-center gap-2"><TrendingUp className="w-5 h-5 text-pink-400" /> Prediksi pengeluaranku</span>}>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Stat label="Rata-rata pengeluaran" value={rupiah(avg)} testid="predict-avg" />
        <Stat label={`Kado (${bdays.length} teman)`} value={rupiah(gifts)} tone="text-violet-500" testid="predict-gifts" />
        <Stat label="Perkiraan total" value={rupiah(total)} tone="text-rose-500" testid="predict-total" />
        <Stat label="Anggaranku" value={budget ? rupiah(budget) : "-"} tone={budget && total > budget ? "text-rose-500" : "text-emerald-500"} testid="predict-budget" />
      </div>
      {budget > 0 && total > budget && <p className="text-sm text-rose-500 mt-3" data-testid="predict-over">Perkiraan melebihi anggaran {rupiah(total - budget)}. Yuk sisihkan dari sekarang!</p>}
      <p className="text-xs text-gray-400 mt-3">Rata-rata dihitung dari pengeluaran 3 bulan sebelumnya, ditambah biaya kado ulang tahun teman di bulan ini.</p>
      {cats.length > 0 && (
        <div className="mt-4 space-y-1.5">
          {cats.map(([c, v]) => <div key={c} className="flex justify-between text-sm"><span className="text-gray-500">{c}</span><span className="font-medium text-gray-700">{rupiah(v)}</span></div>)}
          {gifts > 0 && <div className="flex justify-between text-sm"><span className="text-violet-500">Kado ulang tahun</span><span className="font-medium text-violet-600">{rupiah(gifts)}</span></div>}
        </div>
      )}
      {bdays.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-4">
          {bdays.map((b) => <span key={b.id} className="chip chip-off !text-xs"><Cake className="w-3.5 h-3.5" /> {b.name} · {fmtDate(b.date, "d MMM")}</span>)}
        </div>
      )}
    </Section>
  );
};

const GiftDefault = ({ prefs, save }) => {
  const [v, setV] = useState("");
  useEffect(() => {
    if (prefs) setV(prefs.gift_default ?? 1111);
  }, [prefs]);
  return (
    <div className="flex items-end gap-2">
      <label className="text-xs font-medium text-pink-600 space-y-1 flex-1">
        <span>Biaya kado default (Rp)</span>
        <input className="field" type="number" value={v} onChange={(e) => setV(e.target.value)} data-testid="gift-default-input" />
      </label>
      <Button variant="secondary" className="h-11 rounded-2xl" data-testid="gift-default-save" onClick={async () => { await save({ gift_default: Number(v) }); toast.success("Biaya kado default disimpan"); }}>Simpan</Button>
    </div>
  );
};

export default function BirthdayTab() {
  const [month, setMonth] = useState(monthKey());
  const { items, add, remove } = useCollection("birthdays");
  const { prefs, save } = usePrefs();
  const gift = prefs?.gift_default ?? 1111;
  const sorted = [...items].sort((a, b) => daysUntil(a.date) - daysUntil(b.date));

  return (
    <div className="space-y-4" data-testid="duitku-birthday">
      <div className="flex items-center gap-3">
        <span className="text-sm font-medium text-pink-600">Bulan</span>
        <input type="month" className="field !w-auto" value={month} onChange={(e) => setMonth(e.target.value)} data-testid="birthday-month-input" />
      </div>
      <Prediction birthdays={items} month={month} />
      <Section title={<span className="flex items-center gap-2"><Gift className="w-5 h-5 text-violet-400" /> Ulang tahun temanku</span>}>
        <div className="max-w-sm mb-4"><GiftDefault prefs={prefs} save={save} /></div>
        <EntryForm
          key={gift}
          testid="birthday-form"
          initial={{ name: "", date: "", gift: String(gift) }}
          fields={[
            { name: "name", label: "Nama teman", required: true, placeholder: "mis. Sari" },
            { name: "date", label: "Tanggal lahir", type: "date", required: true },
            { name: "gift", label: "Biaya kado (Rp)", type: "number", min: 0 },
          ]}
          onSubmit={(v) => add({ name: v.name, date: v.date, gift: v.gift === "" ? gift : Number(v.gift) })}
        />
        <div className="mt-4">
          <ItemList
            testid="birthday-list"
            items={sorted}
            onDelete={remove}
            empty="Belum ada catatan ulang tahun teman."
            render={(b) => {
              const d = daysUntil(b.date);
              return {
                icon: <span className="w-9 h-9 rounded-2xl bg-violet-100 text-violet-500 grid place-items-center shrink-0"><Cake className="w-4 h-4" /></span>,
                title: b.name,
                subtitle: `${fmtDate(b.date, "d MMMM")} · ${d === 0 ? "Hari ini!" : `${d} hari lagi`}`,
                right: <span className="text-sm font-semibold text-violet-500 whitespace-nowrap">{rupiah(b.gift)}</span>,
              };
            }}
          />
        </div>
      </Section>
    </div>
  );
}
